import { AfterViewInit, Component, DestroyRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { VerifyMfaViewModel } from './verify-mfa-view-model';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { PortalService } from '../../../../../services/portal/portal.service';
import { AuthCodeRequest } from '../../../../../models/users/requests/auth-code-request';
import { ButtonTypeEnum } from '../../../../../ui/buttons/button/button-type.enum';
import { TextInputComponent } from '../../../../../ui/form-components/text-input/text-input.component';

@Component({
  selector: 'app-verify-mfa',
  templateUrl: './verify-mfa.component.html',
  providers: [VerifyMfaViewModel],
  styleUrls: ['./verify-mfa.component.scss', '../auth.component.scss']
})
export class VerifyMfaComponent implements AfterViewInit {
  private viewModel = inject(VerifyMfaViewModel);
  private portalService = inject(PortalService);
  private destroyRef = inject(DestroyRef);

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  @ViewChild('textInput', { static: true }) textInput!: TextInputComponent;

  private listenToLoadingOpts = this.viewModel.loadingOpts$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(opts => this.loadingOpts.emit(opts));

  public formSubmitted: boolean = false;

  public formGroup = this.viewModel.formGroup;
  public subText$ = this.viewModel.subText$;

  public customErrorMessages = {
    timeBasedOneTimePassword: {
      required: $localize`Verification code is required`,
      minlength: $localize`Verification code must be 6 digits`,
      maxlength: $localize`Verification code must be 6 digits`
    }
  };

  public ngAfterViewInit(): void {
    this.textInput.input.nativeElement.focus();
  }

  public verifyMfa(): void {
    this.formSubmitted = true;
    if (!this.formGroup.valid) return;
    combineLatest([this.viewModel.user$, this.portalService.portalType$])
      .pipe(take(1))
      .subscribe(([user, portalType]) => {
        const { timeBasedOneTimePassword } = this.formGroup.getRawValue();
        const req = new AuthCodeRequest(
          user?.email ?? '',
          timeBasedOneTimePassword,
          user?.challengeName ?? null,
          user?.sessionId ?? ''
        );
        this.viewModel.verifyMfa(req, portalType);
      });
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
